import React, { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Download, ArrowLeft } from "lucide-react";
import { fmtPct, FLAG_TEXT } from "@/lib/prime";
import { toast } from "sonner";

const TABS = ["matched", "unchanged", "ambiguous", "unmatched", "invalid"];
const LABEL = { matched: "Matched", unchanged: "Unchanged", ambiguous: "Ambiguous", unmatched: "Unmatched", invalid: "Invalid" };
const csvCell = (v) => { let s = String(v ?? ""); if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`; return `"${s.replace(/"/g, '""')}"`; };

export function errorReport(rows, excluded) {
  const ex = Object.fromEntries((excluded || []).map((e) => [e.row, e.reason]));
  const lines = [["Row", "Status", "Agent Code", "Person ID", "Agent Name", "Reporting Month", "Reason"].join(",")];
  rows.filter((r) => !["matched", "unchanged", "applied"].includes(r.status) || ex[r.row]).forEach((r) =>
    lines.push([r.row, r.status, r.identifiers?.agent_code, r.identifiers?.person_id, r.identifiers?.agent_name, r.period, ex[r.row] || (r.reasons || []).join("; ")].map(csvCell).join(",")));
  return lines.join("\n");
}

const val = (v) => (v ? `${v.level || "—"} · ${fmtPct(v.score_pct) || "—"}` : "No result");

function Row({ r, approve, setApprove, res, setRes }) {
  return (
    <tr className="border-t border-slate-100 align-top" data-testid={`prime-row-${r.row}`}>
      <td className="px-2 py-1.5 text-slate-400">{r.row}</td>
      <td className="px-2 py-1.5">{r.identifiers?.agent_code || r.identifiers?.person_id}<div className="text-[11px] text-slate-500">{r.identifiers?.agent_name}</div></td>
      <td className="px-2 py-1.5">{r.business_name || "—"}</td>
      <td className="px-2 py-1.5">{r.period}</td>
      <td className="px-2 py-1.5 text-slate-500">{r.status === "matched" || r.status === "unchanged" ? val(r.previous) : ""}</td>
      <td className="px-2 py-1.5 font-medium">{r.result ? val(r.result) : r.proposed ? val(r.proposed) : ""}
        {r.normalized_raw && <div className="text-[10px] text-slate-400">source {r.source_values?.prime_score || ""} → {Number(r.normalized_raw).toFixed(3)}%</div>}
        {r.action === "clear" && <div className="text-[10px] text-rose-600 font-semibold">Clears a stored value</div>}
        {r.is_correction && <div className="text-[10px] text-amber-700 font-semibold">Correction to an existing month</div>}</td>
      <td className="px-2 py-1.5 text-xs">
        {(r.reasons || []).map((x) => <div key={x} className="text-rose-700">{x}</div>)}
        {(r.flags || []).map((f) => <div key={f} className="text-amber-700">{FLAG_TEXT[f] || f}</div>)}
        {r.status === "matched" && r.flags?.length > 0 && <label className="block mt-1"><input type="checkbox" checked={!!approve[r.row]} onChange={() => setApprove({ ...approve, [r.row]: !approve[r.row] })} data-testid={`prime-approve-${r.row}`} /> Approve after review</label>}
        {r.status === "ambiguous" && r.options?.length > 0 && (
          <select value={res[r.row] || ""} onChange={(e) => setRes({ ...res, [r.row]: e.target.value })} className="mt-1 h-8 rounded border border-slate-200 text-xs" data-testid={`prime-resolve-${r.row}`}>
            <option value="">Leave excluded</option>{r.options.map((o) => <option key={o.business_id} value={o.business_id}>{o.business_name} ({o.owners.join(", ")})</option>)}</select>)}
      </td>
    </tr>
  );
}

export function PrimeImportPreview({ batch, onBack, onApplied }) {
  const [tab, setTab] = useState(() => TABS.find((t) => batch.counts[t] > 0) || "matched");
  const [approve, setApprove] = useState({});
  const [res, setRes] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const c = batch.counts;
  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/prime/import/${batch.id}/apply`, { approve_rows: Object.keys(approve).filter((k) => approve[k]).map(Number), resolutions: res });
      setResult(data); toast.success(`${data.applied} results updated`); onApplied();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const rows = batch.rows.filter((r) => r.status === tab);
  return (
    <div className="space-y-3" data-testid="prime-preview">
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <p className="text-sm text-slate-700"><b>{batch.organization_name}</b> · {batch.filename} · reporting {batch.periods.join(", ") || "—"}</p>
        <div className="flex flex-wrap gap-2 mt-2 text-xs" data-testid="prime-preview-counts">
          {TABS.map((t) => <span key={t} className="rounded-full bg-slate-100 px-2.5 py-1">{LABEL[t]}: <b>{c[t]}</b></span>)}
          <span className="rounded-full bg-amber-100 text-amber-800 px-2.5 py-1">Needs review: <b>{c.needs_review}</b></span>
          <span className="rounded-full bg-[#006090]/10 text-[#006090] px-2.5 py-1">Corrections: <b>{c.corrections}</b></span>
        </div>
        {batch.warnings.map((w) => <p key={w} className="text-xs text-amber-700 mt-2" data-testid="prime-preview-warning">{w}</p>)}
      </div>
      <div className="flex flex-wrap gap-1">{TABS.map((t) => (
        <button key={t} onClick={() => setTab(t)} className={`text-xs rounded-full px-3 py-1 border ${tab === t ? "bg-[#013A5A] text-white border-[#013A5A]" : "bg-white border-slate-200"}`} data-testid={`prime-preview-tab-${t}`}>{LABEL[t]} ({c[t]})</button>))}</div>
      <div className="overflow-auto rounded-lg border border-slate-200 bg-white">
        <table className="min-w-full text-sm"><thead className="bg-slate-50 text-xs text-slate-500"><tr>
          <th className="px-2 py-2 text-left">Row</th><th className="px-2 text-left">Agent</th><th className="px-2 text-left">Agency</th><th className="px-2 text-left">Month</th>
          <th className="px-2 text-left">Previous</th><th className="px-2 text-left">Proposed</th><th className="px-2 text-left">Notes</th></tr></thead>
          <tbody>{rows.length === 0 ? <tr><td colSpan={7} className="p-4 text-center text-slate-400 text-xs">No rows</td></tr> : rows.map((r) => <Row key={r.row} r={r} approve={approve} setApprove={setApprove} res={res} setRes={setRes} />)}</tbody></table>
      </div>
      {result ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm" data-testid="prime-apply-result">
          <p className="font-semibold text-emerald-800 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" />{result.applied} results updated · {result.excluded.length} rows excluded</p>
          {result.excluded.length > 0 && <Button variant="outline" size="sm" className="mt-2" onClick={() => downloadCsv(batch, result)} data-testid="prime-error-report"><Download className="w-4 h-4 mr-1" />Download error report</Button>}
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={onBack} data-testid="prime-preview-back"><ArrowLeft className="w-4 h-4 mr-1" />Back to mapping</Button>
          <Button variant="outline" onClick={() => downloadCsv(batch, null)} data-testid="prime-preview-error-report"><Download className="w-4 h-4 mr-1" />Error report</Button>
          <Button onClick={apply} disabled={busy || (c.matched + Object.keys(res).length) === 0} className="bg-brand hover:bg-brand-strong" data-testid="prime-apply-btn">Apply Updates</Button>
        </div>)}
    </div>
  );
}

function downloadCsv(batch, result) {
  const url = URL.createObjectURL(new Blob([errorReport(batch.rows, result?.excluded)], { type: "text/csv" }));
  const a = document.createElement("a"); a.href = url; a.download = `prime-import-errors-${batch.id.slice(0, 8)}.csv`; a.click(); URL.revokeObjectURL(url);
}
